import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumKegstarContainerType } from "./enums/enumKegstarContainerType.generated";
import { EnumPackageTypeCategoryType } from "./enums/enumPackageTypeCategoryType.generated";

// Model Objects

export class PackageType extends ModelObject<PackageType> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Package Type
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    private _litres: number | null = null;
    /**
    * Litres usually dispensed into this package, if relevant
    * Greater Than : 0
    */
    public get litres(): number | null { return this._litres }
    public set litres(value: number | null) {
      this._litres = value === undefined ? null : value;
    }
    /**
    * Package is normally traceable, so expect it to be scanned out on orders.
    */
    public isTraceable: boolean = false;
    /**
    * Package is normally returnable, so expect it to be scanned out and in.
    */
    public isReturnable: boolean = false;
    private _defaultReturnableLabelDocumentTemplateId: Guid | null = null;
    /**
    * Label document template that the package type will use for returnable labels.
    */
    public get defaultReturnableLabelDocumentTemplateId(): Guid | null { return this._defaultReturnableLabelDocumentTemplateId }
    public set defaultReturnableLabelDocumentTemplateId(value: Guid | null) {
      this._defaultReturnableLabelDocumentTemplateId = value === undefined ? null : value;
    }
    private _defaultNonReturnableLabelDocumentTemplateId: Guid | null = null;
    /**
    * Label document template that the package type will use for non-returnable labels.
    */
    public get defaultNonReturnableLabelDocumentTemplateId(): Guid | null { return this._defaultNonReturnableLabelDocumentTemplateId }
    public set defaultNonReturnableLabelDocumentTemplateId(value: Guid | null) {
      this._defaultNonReturnableLabelDocumentTemplateId = value === undefined ? null : value;
    }
    public isHidden: boolean = false;
    /**
    * Set true if this is designed to be connected to a pump or pressurised gas system, such as those used in pubs. Some duty systems have different duty rates for beer packaged in these manners for selling to pubs.
    */
    public isDesignedForPump: boolean = false;
    private _finalUnitLitres: number | null = null;
    /**
    * Litres held by the final consumable packaged unit, if relevant. For example, a package type representing a case of 12 x 500ml bottles would have a final unit litres of 0.5. Some duty systems have different rates depending upon this value.
    * Greater Than : 0
    */
    public get finalUnitLitres(): number | null { return this._finalUnitLitres }
    public set finalUnitLitres(value: number | null) {
      this._finalUnitLitres = value === undefined ? null : value;
    }
    /**
    * Kegstar container type if applicable, otherwise default to None.
    */
    public kegstarContainerType: EnumKegstarContainerType = EnumKegstarContainerType.none;
    /**
    * Category type of this package type.
    */
    public categoryType: EnumPackageTypeCategoryType = EnumPackageTypeCategoryType.unknown;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.litres = jsonSerializedObject.litres == null ? null : jsonSerializedObject.litres;
          this.isTraceable = jsonSerializedObject.isTraceable;
          this.isReturnable = jsonSerializedObject.isReturnable;
          this.defaultReturnableLabelDocumentTemplateId = jsonSerializedObject.defaultReturnableLabelDocumentTemplateId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultReturnableLabelDocumentTemplateId);
          this.defaultNonReturnableLabelDocumentTemplateId = jsonSerializedObject.defaultNonReturnableLabelDocumentTemplateId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultNonReturnableLabelDocumentTemplateId);
          this.isHidden = jsonSerializedObject.isHidden;
          this.isDesignedForPump = jsonSerializedObject.isDesignedForPump;
          this.finalUnitLitres = jsonSerializedObject.finalUnitLitres == null ? null : jsonSerializedObject.finalUnitLitres;
          this.kegstarContainerType = EnumKegstarContainerType.getByValue(jsonSerializedObject.kegstarContainerType, this.kegstarContainerType);
          this.categoryType = EnumPackageTypeCategoryType.getByValue(jsonSerializedObject.categoryType, this.categoryType);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.litres = this.litres == null ? null : this.litres;
        toRet.isTraceable = this.isTraceable;
        toRet.isReturnable = this.isReturnable;
        toRet.defaultReturnableLabelDocumentTemplateId = this.defaultReturnableLabelDocumentTemplateId == null ? null : this.defaultReturnableLabelDocumentTemplateId.toString();
        toRet.defaultNonReturnableLabelDocumentTemplateId = this.defaultNonReturnableLabelDocumentTemplateId == null ? null : this.defaultNonReturnableLabelDocumentTemplateId.toString();
        toRet.isHidden = this.isHidden;
        toRet.isDesignedForPump = this.isDesignedForPump;
        toRet.finalUnitLitres = this.finalUnitLitres == null ? null : this.finalUnitLitres;
        toRet.kegstarContainerType = this.kegstarContainerType.value;
        toRet.categoryType = this.categoryType.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "PackageType.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "litres", rule: (v: any) => v == null || v > 0 || "Litres is not greater than minimum allowed value: 0"},
            {field: "defaultReturnableLabelDocumentTemplateId", rule: (v: any) => v == null || !v.isEmpty() || "PackageType.DefaultReturnableLabelDocumentTemplateId cannot be empty"},
            {field: "defaultNonReturnableLabelDocumentTemplateId", rule: (v: any) => v == null || !v.isEmpty() || "PackageType.DefaultNonReturnableLabelDocumentTemplateId cannot be empty"},
            {field: "finalUnitLitres", rule: (v: any) => v == null || v > 0 || "FinalUnitLitres is not greater than minimum allowed value: 0"},
    ];
}


export class PackageTypeService extends ServiceBase {

  public async getAllPackageTypes(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PackageType[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.packageTypes.map((v: any) => new PackageType(v));;
    return this.makeJsonRequest("packageType/v1/getAllPackageTypes", req, false, extractor, opts);
  };

  public async getPackageType(tenantId: Guid, packageTypeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PackageType> {
    const req = {
    tenantId:tenantId.toString(),
    packageTypeId:packageTypeId.toString(),
    };
    const extractor = (response:any) => new PackageType(response.packageType);;
    return this.makeJsonRequest("packageType/v1/getPackageType", req, false, extractor, opts);
  };

  public async getPackageTypes(tenantId: Guid, packageTypeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PackageType[]> {
    const req = {
    tenantId:tenantId.toString(),
    packageTypeIds:packageTypeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.packageTypes.map((v: any) => new PackageType(v));;
    return this.makeJsonRequest("packageType/v1/getPackageTypes", req, false, extractor, opts);
  };

  public async savePackageType(tenantId: Guid, packageType: PackageType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    packageType:packageType.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("packageType/v1/savePackageType", req, true, extractor, opts);
  };

  public async setPackageTypeIsHidden(tenantId: Guid, packageTypeId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    packageTypeId:packageTypeId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("packageType/v1/setPackageTypeIsHidden", req, true, extractor, opts);
  };
}

export const packageTypeService = new PackageTypeService();